import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Heart, Sparkles, Check, X, ChevronRight, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function localDayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function localNowIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * Two-in-one: a small banner + a fullscreen hybrid lock overlay.
 * - Polls /api/tamagotchi/lock-status every 30s.
 * - Soft nudge 0–5 min after window time → small banner on dashboard.
 * - Hard lock 5–60 min → blocks the whole app until user completes OR skips.
 * - Expired (>60 min) → backend auto-marks missed, banner disappears.
 */
export default function TamagotchiCareWidget({ pet, onUpdate }) {
  const [todayData, setTodayData] = useState(null);  // full /today response
  const [lock, setLock] = useState({ is_locked: false, soft: false });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const pollRef = useRef(null);

  const loadToday = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/tamagotchi/today`,
        { day_key: localDayKey(), local_now: localNowIso() },
        { withCredentials: true });
      setTodayData(data);
    } catch { /* silent */ }
  }, []);

  const loadLock = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/tamagotchi/lock-status`,
        { day_key: localDayKey(), local_now: localNowIso() },
        { withCredentials: true });
      setLock(data || { is_locked: false, soft: false });
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    loadToday();
    loadLock();
    pollRef.current = setInterval(() => { loadToday(); loadLock(); }, 30000);
    const onVis = () => { if (document.visibilityState === 'visible') { loadToday(); loadLock(); } };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [loadToday, loadLock]);

  const check = async (windowId, needId) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/tamagotchi/check`,
        { window_id: windowId, need: needId, day_key: localDayKey(), local_now: localNowIso() },
        { withCredentials: true });
      if (!data.already_done) {
        toast.success(`+${data.points}p · ${data.emoji} ${data.label}`, { duration: 2000 });
      }
      await Promise.all([loadToday(), loadLock()]);
      onUpdate?.();
    } catch (e) {
      toast.error('Kunde inte bocka av');
    } finally {
      setLoading(false);
    }
  };

  const uncheck = async (windowId, needId) => {
    setLoading(true);
    try {
      await axios.post(`${API}/tamagotchi/uncheck`,
        { window_id: windowId, need: needId, day_key: localDayKey() },
        { withCredentials: true });
      await Promise.all([loadToday(), loadLock()]);
      onUpdate?.();
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const skip = async (windowId) => {
    setLoading(true);
    try {
      await axios.post(`${API}/tamagotchi/skip`,
        { window_id: windowId, day_key: localDayKey(), local_now: localNowIso() },
        { withCredentials: true });
      toast('Lugnt. Vi ses nästa gång 💛', { duration: 2000 });
      await Promise.all([loadToday(), loadLock()]);
      onUpdate?.();
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  // ----- Hard lock overlay -----
  if (lock.is_locked && lock.window) {
    return (
      <HardLockOverlay
        pet={pet}
        lock={lock}
        todayData={todayData}
        onCheck={check}
        onSkip={() => skip(lock.window.id)}
        loading={loading}
      />
    );
  }

  // ----- Soft nudge -----
  if (lock.soft && lock.window) {
    const w = todayData?.windows?.find((x) => x.id === lock.window.id);
    return (
      <SoftNudgeBanner
        pet={pet}
        lock={lock}
        window_={w}
        onCheck={check}
        onDismiss={() => setExpanded(false)}
      />
    );
  }

  // ----- Compact dashboard summary -----
  if (!todayData?.windows?.length) return null;
  const totalNeeds = todayData.windows.length * 4;
  const doneCount = todayData.windows.reduce((acc, w) => acc + w.needs.filter((n) => n.done).length, 0);
  const pct = Math.round((doneCount / totalNeeds) * 100);
  const activeWindow = todayData.windows.find((w) => w.phase === 'soft' || w.phase === 'hard');
  const nextUpcoming = todayData.windows.find((w) => w.phase === 'upcoming');

  return (
    <div className="neo-card p-3 bg-gradient-to-br from-[#FFF5E6] via-white to-[#FFE8F5] mb-4" data-testid="tamagotchi-widget">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-3 flex-1 text-left"
          data-testid="tamagotchi-toggle"
        >
          <div className="w-10 h-10 rounded-full bg-[#FDF0D5] border-2 border-black flex items-center justify-center text-xl shrink-0">
            🐕
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold">
              {pet?.name || 'Min kompis'} · Dagens vård {doneCount}/{totalNeeds}
            </p>
            <div className="h-2 rounded-full bg-black/10 mt-1 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#FFC8DD] to-[#C8B6FF]" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
      </div>
      {!expanded && (activeWindow || nextUpcoming) && (
        <p className="text-[11px] text-gray-600 mt-1 ml-13">
          {activeWindow ? `${activeWindow.emoji} ${activeWindow.label} pågår — ${activeWindow.needs.filter((n) => n.done).length}/4` : `Nästa: ${nextUpcoming.emoji} ${nextUpcoming.label} kl ${nextUpcoming.time}`}
        </p>
      )}

      {expanded && (
        <div className="mt-3 grid sm:grid-cols-2 gap-2">
          {todayData.windows.map((w) => (
            <WindowCard
              key={w.id}
              window_={w}
              onCheck={check}
              onUncheck={uncheck}
              onSkip={() => skip(w.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}


function WindowCard({ window_: w, onCheck, onUncheck, onSkip }) {
  const isActive = w.phase === 'soft' || w.phase === 'hard';
  const isExpired = w.phase === 'expired';
  const done = w.needs.filter((n) => n.done).length;
  return (
    <div
      className={`neo-card p-2.5 ${isActive ? 'bg-[#FFF0F6] ring-2 ring-[#FFC8DD]' : isExpired ? 'bg-gray-50 opacity-80' : 'bg-white'}`}
      data-testid={`tamagotchi-window-${w.id}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs font-bold">{w.emoji} {w.label} <span className="opacity-60">{w.time}</span></p>
        <span className="text-[10px] opacity-60">{done}/4</span>
      </div>
      <div className="grid grid-cols-2 gap-1">
        {w.needs.map((n) => (
          <button
            key={n.id}
            onClick={() => (n.done ? onUncheck(w.id, n.id) : onCheck(w.id, n.id))}
            className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border-2 border-black transition-all ${n.done ? 'bg-[#B9FBC0]' : n.missed || n.skipped ? 'bg-gray-100 opacity-60' : 'bg-white hover:bg-[#FFFEF5]'}`}
            data-testid={`tamagotchi-need-${w.id}-${n.id}`}
            title={n.missed ? 'Missat denna gång 💛' : n.skipped ? 'Hoppat över' : n.done ? 'Klickat klart' : 'Klicka för att bocka av'}
          >
            <span>{n.emoji}</span>
            <span className={n.done ? 'line-through' : ''}>{n.label}</span>
            {n.done && <Check className="w-3 h-3 ml-auto" strokeWidth={3} />}
          </button>
        ))}
      </div>
      {isActive && w.any_missed === false && done < 4 && (
        <button
          onClick={onSkip}
          className="w-full mt-2 text-[10px] text-gray-500 hover:text-gray-700"
          data-testid={`tamagotchi-skip-${w.id}`}
        >
          Jag kan inte just nu 💛
        </button>
      )}
    </div>
  );
}


function SoftNudgeBanner({ pet, lock, window_: w, onCheck }) {
  const undone = (w?.needs || []).filter((n) => !n.done && !n.skipped && !n.missed);
  return (
    <div
      className="neo-card p-3 mb-4 bg-gradient-to-br from-[#FFE8F5] to-[#FFF5E6] relative overflow-hidden"
      data-testid="tamagotchi-soft-nudge"
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-[#FFC8DD] border-2 border-black flex items-center justify-center text-2xl shrink-0 animate-pet-bob">
          🐕
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase opacity-60 flex items-center gap-1">
            <Heart className="w-3 h-3 text-[#FF8FA3]" /> {pet?.name || 'Din corgi'} säger · {w?.emoji} {w?.label}
          </p>
          <p className="text-sm font-bold mt-0.5">{lock.message}</p>
          <div className="flex flex-wrap gap-1 mt-2">
            {undone.map((n) => (
              <button
                key={n.id}
                onClick={() => onCheck(w.id, n.id)}
                className="neo-btn px-2 py-0.5 bg-white text-[11px] flex items-center gap-1"
                data-testid={`tamagotchi-nudge-${n.id}`}
              >
                <span>{n.emoji}</span><span>{n.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes pet-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
        .animate-pet-bob { animation: pet-bob 1.4s ease-in-out infinite; }
      `}</style>
    </div>
  );
}


function HardLockOverlay({ pet, lock, todayData, onCheck, onSkip, loading }) {
  const w = todayData?.windows?.find((x) => x.id === lock.window.id);
  const undone = (w?.needs || []).filter((n) => !n.done);
  const done = (w?.needs || []).filter((n) => n.done).length;

  return (
    <div
      className="fixed inset-0 z-[200] bg-gradient-to-br from-[#FFE8F5] via-[#FFF5E6] to-[#FFE4C4] flex items-center justify-center p-6 overflow-auto"
      data-testid="tamagotchi-hard-lock"
    >
      {/* Ambient paws */}
      <div className="absolute inset-0 pointer-events-none opacity-50">
        {Array.from({ length: 14 }).map((_, i) => (
          <span
            key={i}
            className="absolute text-2xl"
            style={{
              left: `${(i * 37) % 100}%`,
              top: `${(i * 53) % 100}%`,
              animation: `tc-float ${6 + (i % 5)}s ease-in-out infinite`,
              animationDelay: `${i * 0.4}s`,
            }}
          >
            🐾
          </span>
        ))}
      </div>

      <div className="relative max-w-md w-full space-y-5">
        <div className="text-center">
          <div className="mx-auto w-28 h-28 rounded-full bg-[#FDF0D5] border-4 border-black flex items-center justify-center text-6xl shadow-[0_6px_0_0_rgba(0,0,0,1)] animate-tc-wobble">
            🐕
          </div>
          <p className="mt-3 text-xs font-bold uppercase tracking-wider opacity-60">
            {pet?.name || 'Din corgi'} · {w?.emoji} {w?.label} kl {w?.time}
          </p>
          <h2 className="text-2xl font-bold font-['Fredoka'] mt-1">{lock.message}</h2>
          <p className="text-xs text-gray-600 mt-1 flex items-center justify-center gap-1">
            <Clock className="w-3 h-3" /> {done}/4 klara · låst tills du bockat av eller säger "jag kan inte"
          </p>
        </div>

        <div className="neo-card p-3 bg-white">
          <p className="text-xs font-bold mb-2 text-center">Vad behövs just nu?</p>
          <div className="grid grid-cols-2 gap-2">
            {(w?.needs || []).map((n) => (
              <button
                key={n.id}
                onClick={() => !n.done && onCheck(w.id, n.id)}
                disabled={loading || n.done}
                className={`neo-btn p-3 flex items-center justify-between text-sm font-bold transition-all ${n.done ? 'bg-[#B9FBC0] opacity-80' : 'bg-white hover:scale-[1.02]'}`}
                style={!n.done ? { backgroundColor: n.color } : undefined}
                data-testid={`tamagotchi-hard-need-${n.id}`}
              >
                <span className="flex items-center gap-2">
                  <span className="text-lg">{n.emoji}</span>
                  <span className={n.done ? 'line-through' : ''}>{n.label}</span>
                </span>
                {n.done ? <Check className="w-4 h-4" strokeWidth={3} /> : <span className="opacity-40">+{n.points}p</span>}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={onSkip}
          disabled={loading}
          className="w-full neo-btn py-2.5 bg-white text-sm flex items-center justify-center gap-2"
          data-testid="tamagotchi-hard-skip"
        >
          <X className="w-4 h-4" /> Jag kan inte just nu 💛
        </button>
        <p className="text-[10px] text-center text-gray-500 px-4">
          Ingen skam alls. {pet?.name || 'Corgin'} blir lite ledsen men förlåter direkt nästa gång du kommer hem 🌸
        </p>
      </div>

      <style>{`
        @keyframes tc-wobble { 0%,100% { transform: rotate(-3deg); } 50% { transform: rotate(3deg) translateY(-4px); } }
        @keyframes tc-float { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-18px) rotate(15deg); } }
        .animate-tc-wobble { animation: tc-wobble 2.2s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
